import {
	Component, Input, OnDestroy, OnChanges, AfterViewInit, ViewChild, SimpleChanges, OnInit, ChangeDetectorRef
} from '@angular/core';
import { NavigationService } from 'netview';
import { MessageService } from 'base';
import { Device } from 'DiscoveryEngine.TS/Core';
import { List, ValueEncoder } from 'DiscoveryEngine.TS/Utils';
import { IdentityService } from 'identity';
import { DisplayDeviceListEntryDto, GetDeviceResultDto } from 'UvExplorerServer.Dtos.TS/Session';
import { IfEntry, IfTable } from 'DiscoveryEngine.TS/InfoSets/IfTable';
import { ToolsService } from 'tools';
import { DiscResultsService } from 'discovery';
import { ExtraIfInfo, PatchManagerResultDto, SwitchToJackConnection } from 'UvExplorerServer.Dtos.TS/Tools';


class SwitchToJackTableEntry {
	public dataKey: string;
	public ifIndex: string;
	public ifAlias: string;
	public name: string;
	public description: string;
	public jackLabel: string;
	public jackRoom: string;
	public stalePortDays: string;

	public ifIndexTag: number;
	public stalePortDaysTag: number;

	public interfaceTag: IfEntry;
	public get hasInterfaceTag(): boolean {
		return !!this.interfaceTag;
	}
	constructor() {
		this.dataKey = "";

		this.ifIndex = "";
		this.ifAlias = "";
		this.name = "";
		this.description = "";
		this.jackLabel = "";
		this.jackRoom = "";
		this.stalePortDays = "";

		this.ifIndexTag = -1;
		this.stalePortDaysTag = -1;

		this.interfaceTag = null;
	}
}


@Component({
	selector: 'switch-to-jack-lookup-editor',
	templateUrl: './switch-to-jack-lookup-editor.component.html',
	styleUrls: ['./switch-to-jack-lookup-editor.component.scss']
})
export class SwitchToJackLookupEditorComponent implements OnChanges, OnDestroy, OnInit {

	@Input() public deviceGuid: string;

	@Input()
	public ifTableHeightVh: number;

	@Input()
	public ifTableHeightPx: number;

	@Input()
	public ifTableMinHeight: number;

	@Input()
	public visible: boolean;

	public device: Device;
	public deviceName: string;
	public displayDeviceSelector: boolean;

	public cols: any[];
	public entries: SwitchToJackTableEntry[];

	public first: number;

	constructor(
		private navService: NavigationService,
		private messageService: MessageService,
		private discResultsService: DiscResultsService,
		private toolsService: ToolsService,
		private identityService: IdentityService,
		private changeDetector: ChangeDetectorRef
	) {
		this.setDevice(null);
		this.displayDeviceSelector = false;
		this.entries = [];
		this.cols = [
			{ field: 'ifIndex', header: 'IF Index', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'ifAlias', header: 'Alias', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'name', header: 'Name', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'description', header: 'Description', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'jackLabel', header: 'Jack Label', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'jackRoom', header: 'Jack Room', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'stalePortDays', header: '# Days Down', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
		];
		this.first = 0;
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (!!changes["deviceGuid"] || !!changes["visible"]) {
			if (!!this.deviceGuid && this.visible) {
				setTimeout(() => this.getDevice(), 0);
			}
			else {
				this.setDevice(null);
			}
		}
	}

	public ngOnInit(): void {

	}

	public ngOnDestroy(): void {

	}

	public get ifTableHeight(): string {
		let result = `max(calc(${this.ifTableHeightVh}vh + ${this.ifTableHeightPx}px), ${this.ifTableMinHeight}px)`;
		//console.log(result);
		return result;
	}

	public get enableSelectDevice(): boolean {
		return true;
	}

	public get enableRefresh(): boolean {
		return !!this.device;
	}

	public selectDevice(): void {
		this.displayDeviceSelector = true;
	}

	public refresh(): void {
		this.lookup();
	}

	private setDevice(device: Device): void {
		if (device) {
			this.device = device;
			this.deviceName = device.DisplayName;
		}
		else {
			this.device = null;
			this.deviceName = "No device is selected";
		}
	}

	public onDeviceSelectorOk(devices: DisplayDeviceListEntryDto[]): void {
		this.deviceGuid = devices[0].deviceGuid;
		this.getDevice();
	}

	public onDeviceSelectorCancel(): void {
		this.displayDeviceSelector = false;
	}

	private getDevice(): void {

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		let deviceGuids = new List<string>();
		deviceGuids.Add(this.deviceGuid);

		this.messageService.startWork();
		this.discResultsService.getDevice(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, "network", deviceGuids, true).subscribe(
				(result: GetDeviceResultDto) => {
					this.messageService.endWork();
					if (result.HasDevices) {
						this.setDevice(result.Devices.Get(0));
						this.lookup();
						this.changeDetector.detectChanges();
					}
					else {
						this.messageService.displayErrorMessage("Could not retrieve selected device");
					}
				},
				(error?) => {
					this.messageService.endWork();
					this.messageService.displayErrorMessage("Could not retrieve selected device");
				});
	}

	private lookup(): void {

		if (!!this.device && this.device.HasIfTable) {
			let sessionGuid = this.identityService.sessionGuid;
			let netViewGuid = this.navService.location.netViewGuid;
			let agentGuid = this.navService.location.agentGuid;
			let schedDiscGuid = this.navService.location.schedDiscGuid;
			let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;
			let doLive = false;

			this.messageService.startWork();
			this.toolsService.lookupSwitchToJack(sessionGuid, netViewGuid, agentGuid, schedDiscGuid, schedDiscRsltGuid,
				this.deviceGuid, doLive).subscribe(
					(result: PatchManagerResultDto) => {
						this.messageService.endWork();
						if (!!this.device && this.device.HasIfTable) {
							this.rebuildEntries(result);
							if (!result.switchToJackConnections || result.switchToJackConnections.length == 0) {
								this.messageService.displayErrorMessage("Could not retrieve jack information for this device");
							}
						}
					},
					(error) => {
						this.messageService.endWork();
						this.rebuildEntries(null);
						this.messageService.displayErrorMessage("Could not retrieve jack information for this device");
					});
		}
		else {
			this.messageService.displayErrorMessage("There is no interface information for this device");
		}
	}

	private rebuildEntries(result: PatchManagerResultDto): void {
		if (!!this.device && this.device.HasIfTable) {

			this.entries = [];

			this.device.IfTable.ForEach((ifEntry: IfEntry) => {
				if (!!result && !!result.extraIfInfo) {
					let info = result.extraIfInfo.find((elem: ExtraIfInfo) => {
						return (elem.ifIndex == ifEntry.IfIndex);
					});
					if (!!info && !info.hasBridgePort) {
						return;
					}
				}

				let tableEntry = new SwitchToJackTableEntry();
				tableEntry.ifIndex = ifEntry.IfIndex.toString();
				tableEntry.ifAlias = ifEntry.Alias;
				tableEntry.name = ifEntry.Name;
				tableEntry.description = ifEntry.Description;
				tableEntry.jackLabel = "";
				tableEntry.jackRoom = "";

				tableEntry.ifIndexTag = ifEntry.IfIndex;
				tableEntry.interfaceTag = ifEntry;

				if (!!result && !!result.switchToJackConnections) {
					let s2jConnection: SwitchToJackConnection = this.lookupConnection(
						result.switchToJackConnections, ifEntry.Name, ifEntry.IfIndex.toString());

					if (s2jConnection) {
						tableEntry.jackLabel = s2jConnection.jack_label;
						tableEntry.jackRoom = s2jConnection.jack_room;
						tableEntry.stalePortDays = s2jConnection.stale_port_days;
						if (s2jConnection.stale_port_days) {
							tableEntry.stalePortDaysTag = parseInt(s2jConnection.stale_port_days);
						}
					}
				}

				this.entries.push(tableEntry);
			});

			this.changeDetector.detectChanges();
		}
	}

	private isLetter(c) {
		return c.toLowerCase() != c.toUpperCase();
	}

	private stripLeadingChars(value: string): string {

		var s = value;
		while (this.isLetter(s.charAt(0))) {
			s = s.substring(1);
		}

		return s;
    }

	private lookupConnection(s2jConnections: SwitchToJackConnection[], ifName: string, ifIndex: string): SwitchToJackConnection {

		if (s2jConnections) {

			if (ifIndex != '') {
				for (let i = 0; i < s2jConnections.length; ++i) {
					let conn: SwitchToJackConnection = s2jConnections[i];
					if (conn.switch_if_index) {
						if (conn.switch_if_index == ifIndex) {
							return conn;
						}
					}
				}
			}

			let lowerIfName = ifName.toLowerCase();

			if (lowerIfName.indexOf('/') > -1)
				lowerIfName = this.stripLeadingChars(ifName);

			for (let i = 0; i < s2jConnections.length; ++i) {
				let conn: SwitchToJackConnection = s2jConnections[i];
				if (conn.switch_module_port_name) {
					if (conn.switch_module_port_name.toLowerCase() == lowerIfName) {
						return conn;
					}
				}
			}
		}

		return null;
	}

	public onFilterChange(): void {

	}

	public doCustomSort(event): void {
		let comparer = function (a: SwitchToJackTableEntry, b: SwitchToJackTableEntry): number {
			let result = 0;

			switch (event.field) {
				case 'ifIndex':
					if (a.ifIndexTag < b.ifIndexTag) {
						result = -1;
					}
					else if (a.ifIndexTag > b.ifIndexTag) {
						result = 1;
					}
					else {
						result = 0;
					}
					break;
				case 'stalePortDays':
					if (a.stalePortDaysTag < b.stalePortDaysTag) {
						result = -1;
					}
					else if (a.stalePortDaysTag > b.stalePortDaysTag) {
						result = 1;
					}
					else {
						result = 0;
					}
					break;
					break;
				default:
					// Compare string fields
					result = a[event.field].localeCompare(b[event.field]);
					break;
			}

			return result * event.order;
		}

		this.entries.sort(comparer);
	}

}
